import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { AppHeader } from "../../components/AppHeader";
import { Loading, Empty } from "../../components/Loading";
import { JobCard } from "../../components/JobCard";
import { useAuth } from "../../context/AuthContext";
import { CheckCircle2, PackageOpen } from "lucide-react";
import { ProductTypeDonut } from "../../components/ProductTypeDonut";

function Section({ title, count, jobs }) {
  if (jobs.length === 0) return null;
  return (
    <section>
      <h2 className="font-display text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">{title} · {count}</h2>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{jobs.map((j) => <JobCard key={j.id} job={j} basePath="/manager/jobs" />)}</div>
    </section>
  );
}

export default function ManagerHome() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState(null);
  const [ptReport, setPtReport] = useState(null);

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([]));
    const now = new Date();
    api.get("/reports/product-types", { params: { year: now.getFullYear(), month: now.getMonth() + 1 } })
      .then((r) => setPtReport(r.data)).catch(() => {});
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good Morning" : hour < 17 ? "Good Afternoon" : "Good Evening";

  if (jobs === null) return (<><AppHeader title="Copper Craft" subtitle="Manager" /><Loading /></>);

  const grp = (s) => jobs.filter((j) => j.status === s);
  const newJobs = grp("NEW");
  const inProd = [...grp("ACCEPTED"), ...grp("IN PRODUCTION")];
  const ready = grp("READY FOR OWNER CHECK");

  return (
    <>
      <AppHeader title="Copper Craft" subtitle="Manager" />
      <div className="px-4 py-4 space-y-6">
        <div>
          <p className="text-slate-400 text-sm font-medium">{greeting},</p>
          <h1 className="font-display text-2xl font-black text-slate-900">{user?.name}</h1>
        </div>

        {newJobs.length > 0 && (
          <div className="rounded-2xl bg-[#F9EFEA] border border-[#E6A893] p-4">
            <p className="text-sm font-bold text-[#9A462B]">📥 {newJobs.length} new job(s) to accept</p>
          </div>
        )}

        {ready.length > 0 && (
          <div className="rounded-2xl bg-indigo-50 border border-indigo-200 p-4 flex items-center justify-between">
            <p className="text-sm font-bold text-indigo-700 flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> {ready.length} job(s) awaiting Owner check</p>
          </div>
        )}

        {newJobs.length + inProd.length + ready.length === 0 && (
          <Empty icon={PackageOpen} text="No active jobs right now." />
        )}

        <Section title="🔵 New Jobs" count={newJobs.length} jobs={newJobs} />
        <Section title="🟡 In Production" count={inProd.length} jobs={inProd} />
        <Section title="🔍 Ready for Owner Check" count={ready.length} jobs={ready} />

        {/* Monthly product type production (received quantity) */}
        {ptReport && (
          <section>
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">This Month's Production</h2>
            <ProductTypeDonut data={ptReport} testid="manager-product-type-donut" />
          </section>
        )}
      </div>
    </>
  );
}
